import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { apiSource, DataProvider, fixtureSource, pickSource } from '@hana/data';
import { initLocale } from '@hana/i18n';
import { ToastProvider } from '@hana/ui';
import { ZONES } from './zones';
import '@hana/ui/base.css';
import { router } from './router';
import { api } from './auth';
import { registerPwa, syncManifest } from './pwa';
import { API_ROUTES } from './api-routes';
import { registerAdminFixtures } from './admin';

const el = document.getElementById('root');
if (!el) throw new Error('Thiếu #root trong index.html');

// Dữ liệu mẫu của board (tới khi màn chuyển sang worker `api`). Mỗi vùng tự nạp bảng ghi của mình.
const fixtures = fixtureSource({}, { delay: 250 });
for (const z of ZONES) z.fixtures?.((entity, rows) => fixtures.register(entity as never, rows as never));
// Việc & Duyệt + Cài đặt (luồng J) nằm ở Hub, không thuộc vùng nào.
registerAdminFixtures((entity, rows) => fixtures.register(entity as never, rows as never));
// Thực thể nào đã khai route ở api-routes.ts thì đọc từ worker `api` (token Office 365), còn lại dữ liệu mẫu.
// Cờ dev `?fx=1` (hoặc VITE_FIXTURES=1): bỏ route api, mọi thực thể dùng dữ liệu mẫu — cho `pnpm board:shot` (không có worker).
const forceFixtures = import.meta.env.DEV && (import.meta.env.VITE_FIXTURES === '1' || new URLSearchParams(location.search).get('fx') === '1');
const source = forceFixtures ? fixtures : pickSource(apiSource(api, API_ROUTES), fixtures, new Set(Object.keys(API_ROUTES)));
const queries = new QueryClient({
  // Lỗi tải dữ liệu ghi ra console (F12) để chẩn đoán prod.
  queryCache: new QueryCache({ onError: (err, q) => console.warn('[hub/data]', q.queryKey, err) }),
  defaultOptions: { queries: { staleTime: 60_000, retry: 1 } },
});

registerPwa();
syncManifest(location.pathname);
router.subscribe('onResolved', ({ toLocation }) => syncManifest(toLocation.pathname));

initLocale();
createRoot(el).render(
  <StrictMode>
    <QueryClientProvider client={queries}>
      <DataProvider source={source}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </DataProvider>
    </QueryClientProvider>
  </StrictMode>,
);
